import { useEffect, useRef, useState } from "react";
import { Star, Check, Trash2, MessageSquareHeart, ImagePlus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export function AdminFeedback() {
  const [reviews, setReviews] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [wForm, setWForm] = useState({ name: "", rating: 5, comment: "", product_name: "", photo_url: "" });
  const [busy, setBusy] = useState(false);
  const [attachFor, setAttachFor] = useState(null);
  const attachRef = useRef(null);

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => toast.error("Could not load feedback"));
  useEffect(() => { load(); }, []);

  const pending = reviews.filter((r) => !r.approved).length;

  const approve = async (id) => {
    try {
      await api.put(`/admin/reviews/${id}/approve`);
      toast.success("Review published on the shop");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !attachFor) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/reviews/photo", fd);
      await api.put(`/admin/reviews/${attachFor}`, { photo_url: up.data.url });
      toast.success("WhatsApp photo added to the review");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not attach that photo"));
    } finally {
      setBusy(false);
      setAttachFor(null);
      e.target.value = "";
    }
  };

  const onWPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/reviews/photo", fd);
      setWForm((s) => ({ ...s, photo_url: up.data.url }));
      toast.success("Photo attached");
    } catch (err) {
      toast.error(formatApiError(err, "Could not attach that photo"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const addFromWhatsApp = async (e) => {
    e.preventDefault();
    if (!wForm.name.trim() || !wForm.comment.trim()) return toast.error("Customer name and review text are required");
    setBusy(true);
    try {
      await api.post("/admin/reviews", wForm);
      toast.success("Review published on the shop");
      setWForm({ name: "", rating: 5, comment: "", product_name: "", photo_url: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-feedback-panel" className="rounded-3xl border border-gold-line bg-white p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-2xl font-semibold text-maroon-dark flex items-center gap-2">
          <MessageSquareHeart className="w-5 h-5 text-gold-text" />
          Customer Feedback
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          {pending > 0 ? (
            <span data-testid="admin-feedback-pending-count" className="rounded-full bg-gold-soft border border-gold-line px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-maroon">{pending} pending</span>
          ) : null}
          <button type="button" data-testid="admin-feedback-add-toggle" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-maroon hover:bg-maroon-dark text-white px-4 py-2 text-xs font-semibold transition-colors duration-200">
            <Plus className="w-3.5 h-3.5" />
            Add from WhatsApp
          </button>
        </div>
      </div>

      <input data-testid="admin-review-photo-input" ref={attachRef} type="file" accept="image/*" className="hidden" onChange={onAttachFile} />

      {showAdd ? (
        <form data-testid="admin-feedback-add-form" onSubmit={addFromWhatsApp} className="mb-6 space-y-3 rounded-2xl border border-dashed border-gold-line bg-ivory/50 p-4">
          <p className="text-xs text-ink-muted">Customer sent a review on WhatsApp? Save their photo, add their words here and it publishes instantly.</p>
          {wForm.photo_url ? (
            <div className="flex items-center gap-3 rounded-xl border border-gold-line bg-white p-2">
              <img data-testid="admin-feedback-add-photo-preview" src={fileUrl(wForm.photo_url)} alt="Attached" className="h-14 w-12 rounded-lg object-cover" />
              <p className="flex-1 text-xs font-semibold text-ink">Customer photo attached</p>
              <button type="button" data-testid="admin-feedback-add-photo-remove" onClick={() => setWForm((s) => ({ ...s, photo_url: "" }))} className="p-2 rounded-full text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors duration-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label htmlFor="wa-review-photo" data-testid="admin-feedback-add-photo-dropzone" className={`flex items-center gap-3 rounded-xl border border-dashed border-gold-line bg-white px-4 py-3 cursor-pointer hover:border-gold transition-colors duration-200 ${busy ? "opacity-60 pointer-events-none" : ""}`}>
              {busy ? <ImagePlus className="w-5 h-5 text-maroon animate-bounce" /> : <ImagePlus className="w-5 h-5 text-gold-text" />}
              <span className="text-xs text-ink-muted">{busy ? "Attaching..." : "Attach the customer's WhatsApp photo"}</span>
            </label>
          )}
          <input data-testid="admin-feedback-add-photo-input" id="wa-review-photo" type="file" accept="image/*" className="hidden" onChange={onWPhoto} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input data-testid="admin-feedback-add-name-input" value={wForm.name} onChange={(e) => setWForm((s) => ({ ...s, name: e.target.value }))} placeholder="Customer name" className="border-gold-line bg-white" />
            <Input data-testid="admin-feedback-add-product-input" value={wForm.product_name} onChange={(e) => setWForm((s) => ({ ...s, product_name: e.target.value }))} placeholder="Which piece? (optional)" className="border-gold-line bg-white" />
          </div>
          <div className="flex items-center gap-1" data-testid="admin-feedback-add-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`admin-feedback-add-star-${n}`} onClick={() => setWForm((s) => ({ ...s, rating: n }))} aria-label={`${n} star${n > 1 ? "s" : ""}`} className="p-0.5">
                <Star className={`w-6 h-6 transition-transform duration-150 hover:scale-110 ${n <= wForm.rating ? "star-gold" : "text-gold-line"}`} />
              </button>
            ))}
          </div>
          <Textarea data-testid="admin-feedback-add-comment-input" value={wForm.comment} onChange={(e) => setWForm((s) => ({ ...s, comment: e.target.value }))} placeholder="What the customer said..." rows={3} className="border-gold-line bg-white" />
          <div className="flex gap-2">
            <Button type="submit" data-testid="admin-feedback-add-submit-btn" disabled={busy} className="rounded-full bg-maroon hover:bg-maroon-dark text-white font-semibold px-8 disabled:opacity-50">
              {busy ? "Publishing..." : "Publish Review"}
            </Button>
            <Button type="button" data-testid="admin-feedback-add-cancel-btn" onClick={() => setShowAdd(false)} variant="outline" className="rounded-full border-gold-line text-ink-muted">
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {reviews.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-muted">No customer reviews yet — they will appear here for your approval.</p>
      ) : (
        <ul data-testid="admin-feedback-list" className="divide-y divide-gold-line/70">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`admin-review-row-${r.id}`} className="py-3 flex flex-wrap items-start gap-3">
              {r.photo_url ? (
                <img data-testid={`admin-review-photo-${r.id}`} src={fileUrl(r.photo_url)} alt="Customer photo" className="h-14 w-12 rounded-lg object-cover border border-gold-line shrink-0" />
              ) : null}
              <div className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "star-gold" : "text-gold-line"}`} />)}
                  {!r.approved ? <span className="rounded-full bg-gold-soft border border-gold-line px-2 py-0.5 text-[10px] font-bold uppercase text-maroon">Pending</span> : <span className="rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-[10px] font-bold uppercase text-green-700">Published</span>}
                </div>
                <p className="text-sm text-ink mt-1">“{r.comment}”</p>
                <p className="text-[11px] text-ink-muted mt-1">{r.name}{r.product_name ? ` • ${r.product_name}` : ""}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" data-testid={`admin-review-photo-btn-${r.id}`} onClick={() => { setAttachFor(r.id); setTimeout(() => attachRef.current?.click(), 0); }} title={r.photo_url ? "Replace photo" : "Add WhatsApp photo"} disabled={busy} className="p-2 rounded-full text-ink-muted hover:text-maroon hover:bg-gold-soft/60 transition-colors duration-200 disabled:opacity-40">
                  <ImagePlus className="w-4 h-4" />
                </button>
                {!r.approved ? (
                  <button type="button" data-testid={`admin-review-approve-btn-${r.id}`} onClick={() => approve(r.id)} title="Publish on shop" className="p-2 rounded-full text-green-600 hover:bg-green-50 transition-colors duration-200">
                    <Check className="w-4 h-4" />
                  </button>
                ) : null}
                <button type="button" data-testid={`admin-review-delete-btn-${r.id}`} onClick={() => remove(r.id)} title="Delete" className="p-2 rounded-full text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors duration-200">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
